import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Avatar, Button, Input, message, Empty, Spin } from 'antd';
import {
  ArrowLeftOutlined,
  HeartOutlined,
  HeartFilled,
  MessageOutlined,
  ShareAltOutlined,
  LinkOutlined,
  WhatsAppOutlined,
  FacebookFilled,
  TwitterOutlined,
  LinkedinFilled,
  CalendarOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import './Blog.css';
import './BlogPost.css';

const PLACEHOLDER = 'https://placehold.co/600x400/e2e8f0/64748b?text=Blog';

const initials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || 'U';

const readStoredComments = (slug) => {
  try {
    return JSON.parse(localStorage.getItem(`cc_blog_comments_${slug}`) || '[]');
  } catch {
    return [];
  }
};

const currentUserName = () => {
  try {
    const u = JSON.parse(localStorage.getItem('authUser') || 'null');
    return u?.name || u?.full_name || '';
  } catch {
    return '';
  }
};

const ArticleBody = ({ content }) => {
  if (!content) {
    return <p style={{ color: '#64748b' }}>Full article coming soon.</p>;
  }

  if (typeof content === 'string') {
    return <div dangerouslySetInnerHTML={{ __html: content }} />;
  }

  if (Array.isArray(content)) {
    return content.map((b, i) => {
      if (b.type === 'h2') return <h2 key={i}>{b.text}</h2>;
      if (b.type === 'quote') return <blockquote key={i}>{b.text}</blockquote>;
      if (b.type === 'ul')
        return (
          <ul key={i}>
            {b.items.map((it, j) => (
              <li key={j}>{it}</li>
            ))}
          </ul>
        );
      return <p key={i}>{b.text}</p>;
    });
  }

  return <p style={{ color: '#64748b' }}>Full article coming soon.</p>;
};

const BlogPost = () => {
  const { slug } = useParams();
  const apiBase = process.env.REACT_APP_API_URL || '/api';

  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [liked, setLiked] = useState(false);
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    setLiked(localStorage.getItem(`cc_blog_liked_${slug}`) === '1');
    setName(currentUserName());

    const fetchPost = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await fetch(`${apiBase}/blog/${slug}`);
        if (!res.ok) throw new Error('Not found');
        const json = await res.json();
        const postData = json.data;
        setPost(postData);

        setComments(readStoredComments(slug));

        const allRes = await fetch(`${apiBase}/blog`).catch(() => null);
        if (allRes && allRes.ok) {
          const allJson = await allRes.json();
          const allPosts = allJson.data || [];
          const sameCat = allPosts.filter(
            (p) => p.slug !== slug && p.category === postData.category,
          );
          const rest = allPosts.filter((p) => p.slug !== slug && p.category !== postData.category);
          setRelated([...sameCat, ...rest].slice(0, 3));
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug, apiBase]);

  const toggleLike = () => {
    const next = !liked;
    setLiked(next);
    if (next) localStorage.setItem(`cc_blog_liked_${slug}`, '1');
    else localStorage.removeItem(`cc_blog_liked_${slug}`);
  };

  const submitComment = () => {
    const text = draft.trim();
    if (!text) return;
    const entry = {
      id: `u${Date.now()}`,
      name: name.trim() || 'Guest',
      text,
      ts: Date.now(),
    };
    const stored = [entry, ...readStoredComments(slug)];
    localStorage.setItem(`cc_blog_comments_${slug}`, JSON.stringify(stored));
    setComments((prev) => [entry, ...prev]);
    setDraft('');
    message.success('Comment posted');
  };

  const copyLink = async () => {
    const shareUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(shareUrl);
      message.success('Link copied to clipboard');
    } catch {
      message.info(shareUrl);
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const enc = encodeURIComponent;
  const shareItems = [
    { key: 'copy', icon: <LinkOutlined />, label: 'Copy link', onClick: copyLink },
    {
      key: 'whatsapp',
      icon: <WhatsAppOutlined />,
      label: 'WhatsApp',
      onClick: () => window.open(`https://wa.me/?text=${enc((post?.title || '') + ' ' + shareUrl)}`, '_blank'),
    },
    {
      key: 'facebook',
      icon: <FacebookFilled />,
      label: 'Facebook',
      onClick: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`, '_blank'),
    },
    {
      key: 'twitter',
      icon: <TwitterOutlined />,
      label: 'X (Twitter)',
      onClick: () => window.open(`https://twitter.com/intent/tweet?text=${enc(post?.title || '')}&url=${enc(shareUrl)}`, '_blank'),
    },
    {
      key: 'linkedin',
      icon: <LinkedinFilled />,
      label: 'LinkedIn',
      onClick: () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`, '_blank'),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '96px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="home-section" style={{ textAlign: 'center', padding: '80px 24px' }}>
        <Empty description="Article not found" />
        <Link to="/blog">
          <Button type="primary" style={{ marginTop: 16 }} icon={<ArrowLeftOutlined />}>
            Back to Blog
          </Button>
        </Link>
      </div>
    );
  }

  const shareUrlClean = typeof window !== 'undefined' ? `${window.location.origin}/blog/${post.slug}` : '';

  const ActionBar = ({ className = '' }) => (
    <div className={`article-actions ${className}`}>
      <button
        className={`article-action ${liked ? 'is-liked' : ''}`}
        onClick={toggleLike}
        aria-pressed={liked}
      >
        {liked ? <HeartFilled /> : <HeartOutlined />}
      </button>
      <a href="#comments" className="article-action">
        <MessageOutlined />
        <span>{comments.length}</span>
      </a>
      <button className="article-action" onClick={copyLink}>
        <ShareAltOutlined />
      </button>
    </div>
  );

  return (
    <div className="home-page">
      <article className="article-wrap">
        <Link to="/blog" className="article-back">
          <ArrowLeftOutlined /> All articles
        </Link>

        <span className="blog-chip" style={{ marginBottom: 14 }}>
          {post.category}
        </span>
        <h1 className="article-title">{post.title}</h1>
        <p className="article-lede">{post.excerpt}</p>

        <div className="article-byline">
          <Avatar style={{ background: '#0363d9', color: '#fff', flexShrink: 0 }}>
            {initials(post.author)}
          </Avatar>
          <div>
            <div className="article-author">{post.author}</div>
            <div className="article-byline-meta">
              <span>
                <CalendarOutlined />{' '}
                {new Date(post.created_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
              {post.read_time && (
                <span>
                  <ClockCircleOutlined /> {post.read_time}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="article-cover">
          <img
            src={post.image_url || PLACEHOLDER}
            alt={post.title}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER; }}
          />
        </div>

        <div className="article-body">
          <ArticleBody content={post.content} />
        </div>

        <ActionBar className="article-actions-bottom" />

        {/* Comments */}
        <section id="comments" className="article-comments">
          <h3 className="article-comments-title">Comments ({comments.length})</h3>

          <div className="comment-form">
            <Input
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
              className="comment-name"
            />
            <Input.TextArea
              placeholder="Share your thoughts..."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoSize={{ minRows: 3, maxRows: 6 }}
              maxLength={600}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <Button type="primary" onClick={submitComment} disabled={!draft.trim()}>
                Post comment
              </Button>
            </div>
          </div>

          <div className="comment-list">
            {comments.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Be the first to comment" />
            ) : (
              comments.map((c) => (
                <div className="comment-item" key={c.id}>
                  <Avatar
                    size={38}
                    style={{ background: '#e0ecff', color: '#0363d9', flexShrink: 0 }}
                  >
                    {initials(c.name)}
                  </Avatar>
                  <div className="comment-body">
                    <div className="comment-head">
                      <span className="comment-author">{c.name}</span>
                      <span className="comment-time">
                        {new Date(c.ts).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="comment-text">{c.text}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="article-related">
            <h3 className="article-comments-title">Keep reading</h3>
            <div className="article-related-grid">
              {related.map((r) => (
                <Link to={`/blog/${r.slug}`} className="related-card" key={r.slug}>
                  <div className="related-cover">
                    <img
                      src={r.image_url || PLACEHOLDER}
                      alt={r.title}
                      loading="lazy"
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER; }}
                    />
                  </div>
                  <div className="related-body">
                    <span className="related-cat">{r.category}</span>
                    <h4>{r.title}</h4>
                    <span className="related-meta">{r.read_time}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </div>
  );
};

export default BlogPost;
